import { motion } from "framer-motion";
import { Star, Quote } from "lucide-react";
import { useTheme } from "@/components/theme-provider";
import { cn } from "@/lib/utils";

const testimonials = [
  {
    quote: "Numberz Insight transformed our financial operations. Their expertise in tax planning saved us thousands of dollars annually.",
    author: "Sarah Johnson",
    position: "CEO, TechStart Solutions",
    rating: 5,
    image: "https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?q=80&w=1974&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
  },
  {
    quote: "The team's attention to detail and proactive approach to our business registration made the entire process seamless and stress-free.",
    author: "Michael Chen",
    position: "Founder, Artisan Bakery Co.",
    rating: 5,
    image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?q=80&w=1974&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
  },
  {
    quote: "As a small business owner, I needed guidance on tax compliance. Numberz Insight provided clear, actionable advice that kept us compliant.",
    author: "Emma Rodriguez",
    position: "Owner, Coastal Designs",
    rating: 5,
    image: "https://images.unsplash.com/photo-1580489944761-15a19d654956?q=80&w=1961&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
  }
];

const Testimonials = () => {
  const { theme } = useTheme();

  return (
    <section className="py-24 bg-gradient-to-b from-background to-muted/30 dark:from-gray-950 dark:to-gray-900/50">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="text-4xl font-bold mb-5 text-foreground dark:text-white">
              What Our Clients Say
            </h2>
            <div className="w-20 h-1 bg-primary mx-auto mb-5"></div>
            <p className="text-muted-foreground dark:text-gray-300 max-w-2xl mx-auto text-lg">
              Trusted by businesses and individuals across Australia
            </p>
          </motion.div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {testimonials.map((testimonial, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="bg-card dark:bg-gray-800 rounded-xl shadow-lg p-8 border border-border dark:border-gray-700 relative overflow-hidden"
            >
              {/* Background decoration */}
              <div className="absolute top-0 right-0 w-20 h-20 bg-primary/5 dark:bg-primary/10 rounded-bl-3xl"></div>
              
              {/* Quote icon */}
              <div className="absolute top-6 right-6 text-primary/20 dark:text-primary/30">
                <Quote className="w-10 h-10" />
              </div>
              
              {/* Rating */}
              <div className="flex mb-4 text-yellow-400">
                {[...Array(testimonial.rating)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 fill-current" />
                ))}
              </div>
              
              {/* Quote */}
              <p className="text-foreground dark:text-white mb-6 relative z-10">
                "{testimonial.quote}"
              </p>
              
              {/* Author info */}
              <div className="flex items-center mt-6">
                <div className="w-12 h-12 rounded-full overflow-hidden mr-4 border-2 border-primary/20">
                  <img 
                    src={testimonial.image} 
                    alt={testimonial.author}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div>
                  <h4 className="font-semibold text-foreground dark:text-white">
                    {testimonial.author}
                  </h4>
                  <p className="text-sm text-muted-foreground dark:text-gray-400">
                    {testimonial.position}
                  </p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
        
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="mt-16 text-center"
        >
          <div className="inline-block bg-card dark:bg-gray-800 rounded-full px-6 py-3 shadow-md border border-border dark:border-gray-700">
            <p className="text-foreground dark:text-white font-medium">
              Join our <span className="text-primary font-semibold">10,000+</span> satisfied clients today
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Testimonials;
